/* eslint-disable */
// AnnouncementComposer — the window an admin uses to send an announcement.
//
// Mounts through AppOverlays (above the router), opened by a uiStore boolean —
// the same mechanism GlobalSearchMount uses. Not a route: opening it never
// changes what is behind it (Conv #136).
//
// The shell is DnaModalShell, the project's existing dialog primitive: portal to
// body, role=dialog, aria-modal, four close affordances (× · backdrop · ESC ·
// Cancel) and an in-card dirty guard. It takes a `glyph` prop as of this brief
// so the megaphone can replace the default diamond; every other consumer is
// untouched.
//
// ⚠️ THE ESTIMATE IS A CANDIDATE COUNT, NEVER A DELIVERY COUNT. "Goes to up to N
// people" before sending; the real figures come back in the envelope. And
// {ok:true, recipients:0} keeps this dialog OPEN — a green tick on a send that
// reached nobody is the worst thing this screen can do.

import React, { useEffect, useMemo, useState } from 'react';
import DnaModalShell from '../dna/DnaModalShell';
import { Megaphone } from './HeaderBell';
import { NotificationCard } from './NotificationCard';
import { AudiencePicker } from './AudiencePicker';
import AttachPicker from './AttachPicker';
import { useAnnouncementAudience } from '../../hooks/useAnnouncementAudience';
import { useAnnouncementSend } from '../../hooks/useAnnouncementSend';
import ComposerEmailToggle from './ComposerEmailToggle';
import { toPayloadRef } from '../../utils/attachmentRegistry';
import { useNotificationsStore } from '../../stores/notificationsStore';
import styles from './composer.module.css';

const MAX_NOTE = 240;

const PeopleGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
  </svg>
);

export function AnnouncementComposer() {
  const [attached, setAttached] = useState([]);
  const open = useNotificationsStore((s) => s.showComposer);
  const seed = useNotificationsStore((s) => s.composerItem);
  const setShowComposer = useNotificationsStore((s) => s.setShowComposer);

  const [item, setItem] = useState(null);
  const [mode, setMode] = useState('users');
  const [portals, setPortals] = useState([]);
  const [users, setUsers] = useState([]);
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState(null);   // {kind:'error'|'zero', text}

  const hasItem = !!item;
  const typeKey = hasItem ? 'lens.favorited' : 'system.announcement';
  const aud = useAnnouncementAudience({ hasItem, itemPortal: 'lens' });
  const { send, sending } = useAnnouncementSend();
  // null = follow the type. The checkbox sets true/false; until someone touches
  // it the payload carries no `email` key at all, which is what makes "use the
  // type's setting" the default rather than a value this screen has to guess.
  const [emailChoice, setEmailChoice] = useState(null);

  // Seed from the trigger each time it opens, and reset everything else so a
  // previous draft never leaks into a new announcement.
  useEffect(() => {
    if (!open) return;
    // TWO SEED SHAPES, deliberately kept apart.
    //
    // The LEGACY shape has no `kind` and comes from a lens card (LensCard.js:74).
    // It drives the whole lens.favorited path — type_key, entity_id and the
    // auto-composed "<name> marked as favourite" title — and P7 does not touch
    // any of it.
    //
    // The P7 shape carries `kind` and is a normal P6 ATTACHMENT: it goes into
    // `attached`, the type stays system.announcement, and the title stays EMPTY,
    // because a message nobody wrote is a message nobody meant (§5.2).
    const isAttachmentSeed = !!(seed && seed.kind);
    setItem(isAttachmentSeed ? null : (seed || null));
    setAttached(isAttachmentSeed ? [seed] : []);
    setNote('');
    setEmailChoice(null);
    setPortals([]);
    setUsers([]);
    setProblem(null);
  }, [open, seed]);

  // Detaching switches the notification type, which can switch the send from
  // permitted to refused — so the offered modes are re-derived, and a mode that
  // is no longer offered must not stay selected.
  useEffect(() => {
    if (!aud.modes.length) return;
    if (!aud.modes.includes(mode)) setMode(aud.modes[0]);
  }, [aud.modes, mode]);

  const spec = useMemo(() => {
    if (mode === 'all') return {};
    if (mode === 'portal') return { portals };
    return { user_ids: users };
  }, [mode, portals, users]);

  const estimate = aud.estimateFor(mode, spec);
  // Selected somebody, but that somebody is only you. The platform would accept
  // the send and deliver it to nobody, so say why up front instead.
  const onlySelf = aud.selfOnly(mode, spec);
  const title = hasItem ? `${item.name} marked as favourite` : (note.trim().split('\n')[0] || '');
  // When the composer was opened FROM an item, the note's placeholder names it
  // — a prompt, not a pre-filled title (§5.2).
  const seededCode = seed && seed.kind ? seed.code : null;
  const canSubmit = estimate > 0 && !onlySelf && title.trim().length > 0 && !sending;

  const disabledReason = sending ? 'Sending…'
    : onlySelf ? 'You cannot send an announcement only to yourself.'
    : estimate === 0 ? 'Pick at least one recipient'
    : !title.trim() ? 'Attach an item or write a note — there would be nothing to show'
    : '';

  // The preview renders the REAL card, inert. One definition of the card, so the
  // preview cannot drift from what recipients actually get.
  const previewRow = useMemo(() => ({
    id: 'preview',
    notifications: {
      id: 'preview',
      type_key: typeKey,
      severity: 'normal',
      title: title || 'Your announcement',
      body: note.trim() || null,
      // ⚠️ The SHAPE a recipient gets: {attachments:[{kind,id}]} and nothing
      // else. It used to carry {image_url}, which no sender ever wrote and
      // which §2.4 forbids — a URL here is resolved once, by the sender, and
      // then belongs to everyone who reaches the row.
      payload: attached.length ? { attachments: attached.map(toPayloadRef) } : {},
      created_by: null,
      created_at: new Date().toISOString(),
      notification_types: { label_en: hasItem ? 'Lens marked as favorite' : 'Announcement' },
    },
  }), [hasItem, title, note, attached]);

  const close = () => { setAttached([]); setShowComposer(false, null); };

  const onSubmit = async () => {
    setProblem(null);
    // §7 — the override travels in the payload send_notification already takes;
    // its SIGNATURE IS UNCHANGED and there is no p_email argument. An absent key
    // means "use the type's setting", which is why untouched stays absent.
    const payload = attached.length ? { attachments: attached.map(toPayloadRef) } : {};
    if (emailChoice !== null) payload.email = emailChoice;

    const res = await send({
      typeKey,
      title,
      body: note.trim(),
      entityId: hasItem ? String(item.id) : null,
      audienceKind: mode,
      audienceSpec: spec,
      payload,
    });

    if (res.status === 'busy') return;             // the double-click guard held
    if (res.status === 'sent') { close(); return; }
    if (res.status === 'zero') {
      // Accepted, delivered to nobody. Stay open and say so.
      setProblem({ kind: 'zero', text: 'Nobody received this. Everyone you picked either lacks access to this item or has muted this type.' });
      return;
    }
    setProblem({ kind: 'error', text: res.message });
  };

  if (!open) return null;

  // A portal admin with nothing attached cannot send at all — the database
  // refuses a NULL-portal type from them. Say that, rather than showing a picker
  // whose every option would be rejected.
  const blocked = !aud.loading && !aud.canSend;

  return (
    <DnaModalShell
      open={open}
      onClose={close}
      title="New announcement"
      subtitle="Pops up on screen for everyone you pick — wherever they are in the Hub."
      glyph={<Megaphone />}
      onSubmit={onSubmit}
      submitLabel="Send"
      submitting={sending}
      submitDisabled={!canSubmit || blocked}
      isDirty={note.trim().length > 0}
      data-test="announcement-composer"
    >
      <div className={styles.composerRoot}>
        {blocked ? (
          <div className={styles.blocked} data-test="composer-blocked">
            Only a super admin can send a system-wide announcement. Attach an item to
            announce it to your portal.
          </div>
        ) : (
          <>
            {hasItem && (
              <div className={styles.attached} data-test="composer-attached">
                {item.photo
                  ? <img loading="lazy" className={styles.attachedThumb} src={item.photo} alt="" />
                  : <span className={styles.attachedThumb} />}
                <div className={styles.attachedText}>
                  <div className={styles.attachedName}>{item.name}</div>
                  <div className={styles.attachedSub}>Lens · {item.code} · marked Favorite</div>
                </div>
                <button
                  type="button"
                  className={styles.attachedX}
                  onClick={() => setItem(null)}
                  aria-label="Detach item"
                  data-test="composer-detach"
                >×</button>
              </div>
            )}

            <div>
              <div className={styles.label}>Attach — optional</div>
              <AttachPicker attached={attached} onChange={setAttached} />
            </div>

            <AudiencePicker
              modes={aud.modes}
              mode={mode}
              onModeChange={setMode}
              portalOptions={aud.portalOptions}
              selectedPortals={portals}
              onTogglePortal={(id) => setPortals((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id])}
              userOptions={aud.userOptions}
              selectedUsers={users}
              onToggleUser={(id) => setUsers((u) => u.includes(id) ? u.filter((x) => x !== id) : [...u, id])}
              totalPeople={aud.totalPeople}
            />

            <div>
              <div className={styles.label}>Your note — optional</div>
              <textarea
                className={styles.note}
                dir="auto"
                maxLength={MAX_NOTE}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={seededCode ? `What do people need to know about ${seededCode}?` : "Add a line of context — e.g. why this one matters, or what to do next."}
                data-test="composer-note"
              />
              <div className={styles.counter} data-test="composer-counter">{note.length} / {MAX_NOTE}</div>
            </div>

            <div>
              <div className={styles.label}>What they will see</div>
              <div className={styles.previewWrap} data-test="composer-preview">
                <NotificationCard row={previewRow} senderName="You" preview />
              </div>
            </div>

            {problem?.kind === 'error' && (
              <div className={styles.err} data-test="composer-error">{problem.text}</div>
            )}
            {problem?.kind === 'zero' && (
              <div className={styles.warn} data-test="composer-zero">{problem.text}</div>
            )}

            {/* §6.4 — the per-send decision, on top of the per-type one. Beside
                Send rather than buried in the form: it changes what leaves the
                building, and it is the last thing you check before sending. */}
            <ComposerEmailToggle typeKey={typeKey} choice={emailChoice} onChoice={setEmailChoice} />

            {!problem && (
              <div className={styles.estimate} title={disabledReason} data-test="composer-estimate">
                <PeopleGlyph />
                {onlySelf
                  ? <span>You cannot send an announcement only to yourself.</span>
                  : estimate > 0
                    ? <span>Goes to up to <strong>{estimate}</strong> {estimate === 1 ? 'person' : 'people'}</span>
                    : <span>Pick at least one recipient</span>}
              </div>
            )}
          </>
        )}
      </div>
    </DnaModalShell>
  );
}

export default AnnouncementComposer;
